// ABOUTME: OIDC presentation mode for the current browser context — Google returns
// ABOUTME: 403 disallowed_useragent inside embedded webviews, so providers need gating.

import { useEffect, useState } from "react"
import { NativeBridge } from "@/model/NativeBridge"

export type OidcMode = "web" | "native" | "hidden"

const isNativeWrapper = (): boolean => NativeBridge.isAvailable()

// Third-party in-app browsers (Telegram, Meta apps, Android WebView's "wv"
// marker, iOS GSA) also trip Google's embedded-useragent block. They have no
// native hand-off, so provider buttons must be hidden there — email/password
// still works. Same idea as the hub's Telegram-only filter, widened.
const isEmbeddedBrowser = (): boolean => {
  if (isNativeWrapper()) return true
  if (typeof navigator === "undefined") return false
  return /(\bwv\b|Telegram|FBAN|FBAV|Instagram|Line\/|GSA\/)/i.test(navigator.userAgent)
}

let probe: Promise<boolean> | null = null

// True when our wrapper app exposes auth.signInWithProvider
// (system.checkSupport.authSignIn). Old APKs answer without the flag → false.
const canUseNativeOidc = (): Promise<boolean> => {
  if (!isNativeWrapper()) return Promise.resolve(false)
  probe ??= NativeBridge.getInstance()
    .request<Record<string, never>, { authSignIn?: boolean }>("system.checkSupport", {})
    .then((support) => support.authSignIn === true)
    .catch(() => false)
  return probe
}

/**
 * Hand the provider flow to the wrapper app: it opens a fresh Kratos flow in a
 * Chrome Custom Tab and returns the session via the App Link callback (the app
 * then reloads the WebView already authenticated). Fire-and-forget from the
 * form's perspective.
 */
export const signInWithNativeProvider = (provider: string): Promise<void> =>
  NativeBridge.getInstance()
    .request("auth.signInWithProvider", { provider })
    .then(() => undefined)

/**
 * Open the account/security page in the external browser that owns the Kratos
 * session cookie. After a Custom Tab OIDC login the WebView has only the JWT,
 * so cookie-driven settings flows (change password, linked accounts) can't
 * load in-app — the browser that ran the login can serve them.
 */
export const openNativeSecuritySettings = (): Promise<void> =>
  NativeBridge.getInstance()
    .request("auth.openSecuritySettings", {})
    .then(() => undefined)

/**
 * Tell the wrapper app the user has logged out so it drops the persisted native
 * session token. Without this the app re-injects the old session on the next
 * WebView recreate and silently re-authenticates the previous account. Call it
 * AFTER revoking the Kratos session. Best-effort: no-op outside the wrapper, and
 * old APKs without the method simply reject — we swallow that.
 */
export const notifyNativeLoggedOut = (): Promise<void> => {
  if (!isNativeWrapper()) return Promise.resolve()
  return NativeBridge.getInstance()
    .request("auth.logout", {})
    .then(() => undefined)
    .catch(() => undefined)
}

/**
 * How OIDC buttons should render here:
 *  - "web"    — normal Kratos submit (real browsers, desktop and mobile);
 *  - "native" — hand off to the wrapper app (auth.signInWithProvider);
 *  - "hidden" — embedded browser without native hand-off (old APK, Telegram, …).
 * Starts as "hidden" inside embedded browsers and upgrades to "native" once the
 * bridge confirms support — buttons appear, never flash-then-vanish.
 */
export const useOidcMode = (): OidcMode => {
  const [mode, setMode] = useState<OidcMode>(() => (isEmbeddedBrowser() ? "hidden" : "web"))

  useEffect(() => {
    if (!isNativeWrapper()) return
    let alive = true
    void canUseNativeOidc().then((supported) => {
      if (alive && supported) setMode("native")
    })
    return () => {
      alive = false
    }
  }, [])

  return mode
}
